import React, { useMemo } from 'react'
import {
    Box,
    Card,
    CardContent,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import { PieChart } from 'react-minimal-pie-chart'

const DONUT_COLORS = {
    used: '#547D7D',
    committed: '#6570E6',
    remaining: '#EEF2F4',
} as const

const useStyles = makeStyles((theme: Theme) => ({
    card: {
        height: '100%',
    },
    subtitle: {
        color: theme.palette.text.secondary,
        fontSize: '0.75rem',
        marginTop: theme.spacing(0.5),
    },
    chartWrap: {
        position: 'relative',
        height: 192,
        marginTop: theme.spacing(1.5),
    },
    chartCenter: {
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
    },
    remainingValue: {
        fontFamily: 'monospace',
        fontSize: '1.5rem',
        fontWeight: 600,
        lineHeight: 1.2,
    },
    remainingLabel: {
        fontSize: '0.6875rem',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.25),
    },
    legend: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: theme.spacing(1),
        marginTop: theme.spacing(1.5),
        textAlign: 'center',
    },
    legendItem: {
        fontSize: '0.75rem',
    },
    legendDot: {
        width: 8,
        height: 8,
        borderRadius: '50%',
        display: 'inline-block',
        marginRight: theme.spacing(0.75),
    },
    legendLabel: {
        color: theme.palette.text.secondary,
    },
    legendValue: {
        fontFamily: 'monospace',
        fontWeight: 600,
        marginTop: theme.spacing(0.25),
    },
}))

interface MonthlyTokensCardProps {
    monthlyBudget: number | null
    currentBalance: number | null
    tokensSpentThisMonth?: number
}

export const MonthlyTokensCard: React.FC<MonthlyTokensCardProps> = ({
    monthlyBudget,
    currentBalance,
    tokensSpentThisMonth = 0,
}) => {
    const classes = useStyles()

    const { used, committed, remaining, budget } = useMemo(() => {
        const budgetValue = monthlyBudget ?? 0
        const remainingValue = currentBalance ?? 0
        const committedValue = 0
        return {
            used: Math.max(0, tokensSpentThisMonth),
            committed: committedValue,
            remaining: remainingValue,
            budget: budgetValue,
        }
    }, [monthlyBudget, currentBalance, tokensSpentThisMonth])

    const segments = useMemo(() => {
        const total = used + committed + remaining
        if (total <= 0) {
            return [
                { title: 'Remaining', value: 1, color: DONUT_COLORS.remaining },
            ]
        }
        return [
            { title: 'Used', value: used || 0, color: DONUT_COLORS.used },
            {
                title: 'Committed',
                value: committed || 0,
                color: DONUT_COLORS.committed,
            },
            {
                title: 'Remaining',
                value: remaining || 0,
                color: DONUT_COLORS.remaining,
            },
        ].filter((segment) => segment.value > 0)
    }, [used, committed, remaining])

    return (
        <Card className={classes.card} variant="outlined">
            <CardContent>
                <Typography variant="subtitle1">
                    <strong>This month&apos;s tokens</strong>
                </Typography>
                <Typography className={classes.subtitle}>
                    Auto-replenishes on the 1st. Unused tokens don&apos;t roll
                    over.
                </Typography>

                <Box className={classes.chartWrap}>
                    <PieChart data={segments} lineWidth={20} rounded animate />
                    <Box className={classes.chartCenter}>
                        <Typography className={classes.remainingValue}>
                            {remaining.toLocaleString()}
                        </Typography>
                        <Typography className={classes.remainingLabel}>
                            left of {budget.toLocaleString()}
                        </Typography>
                    </Box>
                </Box>

                <Box className={classes.legend}>
                    {([
                        { name: 'Used', value: used, color: DONUT_COLORS.used },
                        {
                            name: 'Committed',
                            value: committed,
                            color: DONUT_COLORS.committed,
                        },
                        {
                            name: 'Remaining',
                            value: remaining,
                            color: DONUT_COLORS.remaining,
                        },
                    ] as const).map((item) => (
                        <Box key={item.name} className={classes.legendItem}>
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                            >
                                <span
                                    className={classes.legendDot}
                                    style={{ backgroundColor: item.color }}
                                />
                                <span className={classes.legendLabel}>
                                    {item.name}
                                </span>
                            </Box>
                            <Typography className={classes.legendValue}>
                                {item.value.toLocaleString()}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </CardContent>
        </Card>
    )
}
